/**
 * Byte-length range check for strings.
 *
 * Ported to TypeScript from the `validator` package (MIT licensed).
 *
 * @module isByteLength
 */

/**
 * Matches each encoded byte in an `encodeURI` result: either a `%XX` escape
 * (one byte) or a single un-escaped character (one byte). Splitting on this and
 * counting the gaps yields the UTF-8 byte length without allocating a Buffer.
 */
const BYTE_LENGTH_REGEXP = /%..|./g;

/** Inclusive byte-count bounds. Omit a bound to leave that side unbounded. */
export interface IsByteLengthOptions {
  /** Minimum number of bytes (inclusive). Defaults to `0`. */
  min?: number;
  /** Maximum number of bytes (inclusive). Defaults to no maximum. */
  max?: number;
}

/**
 * Reports whether a string's UTF-8 byte length falls within `[min, max]`.
 *
 * The length is measured in bytes, not code units, so multi-byte characters
 * (accents, emoji, CJK, …) count for more than one. Useful for fields with a
 * byte budget, such as the 64-byte local part of an email address.
 *
 * @param str - The string to measure.
 * @param options - Inclusive `min`/`max` byte bounds.
 * @returns `true` if the byte length is within bounds.
 *
 * @example
 * ```ts
 * isByteLength("abc", { max: 5 }); // true  (3 bytes)
 * isByteLength("héllo", { max: 5 }); // false (6 bytes — "é" is 2)
 * ```
 */
export default function isByteLength(
  str: string,
  { min = 0, max }: IsByteLengthOptions = {},
) {
  const len = encodeURI(str).split(BYTE_LENGTH_REGEXP).length - 1;
  return len >= min && (max == null || len <= max);
}
